/* ============================================================
   favoritesView — CB-6 (a90f4a83). Pure view model for the
   favorites list page: one row per favorited product/performer,
   newest first. The server re-resolves targets at read time, so
   this module shapes the honest states into calm ru copy:
   - delisted product → «Снят с продажи» badge;
   - retired performer → «Недоступен»;
   - deleted target → muted row «Товар удалён» / «Исполнитель
     недоступен» with a remove-only action (CB-5 posture).
   Light theme, Montserrat, iOS-18 minimal per DESIGN-SYSTEM.md.
   ============================================================ */

import type { FavoriteItem, FavoriteKind, FavoriteList } from '../types/favorite';

/** Local price format (same as types/product formatRub — kept local so this
 * pure module stays node-testable: node --test strips type-only imports but
 * cannot resolve extensionless VALUE imports like '../types/product'.) */
function formatRub(v: number): string {
  return `${v.toLocaleString('ru-RU', { maximumFractionDigits: 0 })} ₽`;
}

export type FavoriteBadgeTone = 'accent' | 'danger' | 'muted';

export interface FavoriteBadge {
  text: string;
  tone: FavoriteBadgeTone;
}

export interface FavoriteRowView {
  key: string;
  kind: FavoriteKind;
  /** '🧊' product / '🏭' performer — neutral glyphs, no emoji noise. */
  glyph: string;
  kindLabel: string;
  title: string;
  subtitle: string;
  priceText: string | null;
  badge: FavoriteBadge | null;
  /** Muted presentation for deleted targets (no navigation). */
  muted: boolean;
  /** Product rows navigate to the product page; provider rows do not. */
  clickable: boolean;
  productId: string | null;
  removeLabel: string;
}

/** Pure view model — trivially unit-testable, no React dependency. */
export function toFavoriteRow(item: FavoriteItem): FavoriteRowView {
  const isProduct = item.kind === 'product';

  let badge: FavoriteBadge | null = null;
  if (item.missing) {
    badge = { text: isProduct ? 'Товар удалён' : 'Исполнитель недоступен', tone: 'danger' };
  } else if (item.isOwnListing) {
    badge = { text: 'Ваш товар', tone: 'muted' };
  } else if (item.unavailable) {
    badge = { text: isProduct ? 'Снят с продажи' : 'Недоступен', tone: 'muted' };
  }

  return {
    key: item.id,
    kind: item.kind,
    glyph: isProduct ? '🧊' : '🏭',
    kindLabel: isProduct ? 'Товар' : 'Исполнитель',
    title: item.title,
    subtitle: item.subtitle,
    priceText: !item.missing && item.priceRub !== null && item.priceRub !== undefined
      ? formatRub(Number(item.priceRub))
      : null,
    badge,
    muted: item.missing,
    clickable: isProduct && !item.missing,
    productId: isProduct && !item.missing ? item.productId : null,
    removeLabel: 'Убрать из избранного',
  };
}

export interface FavoritesPageView {
  rows: FavoriteRowView[];
  headline: string;
  emptyTitle: string;
  emptyHint: string;
}

export function buildFavoritesPageView(list: FavoriteList): FavoritesPageView {
  const rows = (list.items ?? []).map(toFavoriteRow);
  const count = list.totalCount ?? rows.length;
  return {
    rows,
    headline: count > 0 ? `Избранное · ${count}` : 'Избранное',
    emptyTitle: 'В избранном пока пусто',
    emptyHint: 'Откройте магазин или каталог исполнителей и нажмите ♡ — товары и исполнители соберутся здесь',
  };
}

/** Heart button copy/state for the detail pages. */
export function heartLabel(favorited: boolean): string {
  return favorited ? '♥ В избранном' : '♡ В избранное';
}

/** Relative ru «added» timestamps for the list page (muted caption). */
export function addedAtLabel(addedAtUtc: string, now: Date = new Date()): string | null {
  const ts = Date.parse(addedAtUtc);
  if (!Number.isFinite(ts)) return null;
  const minutes = Math.floor((now.getTime() - ts) / 60000);
  if (minutes < 1) return 'только что';
  if (minutes < 60) return `${minutes} мин назад`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} ч назад`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days} дн назад`;
  return new Date(ts).toLocaleDateString('ru-RU');
}
